// The PunchWISE clock status on the On-site team panel — its words, and when
// it is asked for.
//
// Pure: no React, no network, no `document`. The hook in use-team-clock.ts
// hands it the real ones; the tests hand it fakes.
//
// ⚠️ A SLOT WITH NO MEMBER ENTRY RENDERS NOTHING. PunchWISE is not live at
// most stores, and the API leaves out everyone it does not know or does not
// see in use. "Not clocked in" is said only when PunchWISE says `out`.

import type { TeamClockMember, TeamClockResponse } from './types';

/** How often the panel refreshes while the browser tab is visible. */
export const CLOCK_REFRESH_MS = 60_000;

/** What the panel holds: the API's answer, or the request for it failing. */
export type TeamClockView =
  | TeamClockResponse
  | { state: 'unavailable'; cause: 'request_failed'; detail: string };

export interface ClockLine {
  tone: 'in' | 'meal' | 'out';
  text: string;
}

/**
 * One row's status.
 *
 * "Here" needs BOTH ids and needs them equal. When either side does not know
 * its `Facility_Information.ID`, the site is NAMED — "Clocked in at <site>" is
 * true whichever store it is, while a bare "Clocked in" on this store's page
 * would claim the person is here.
 */
export function clockLine(m: TeamClockMember, facilityFiId: number | null): ClockLine {
  if (m.clock === 'out') return { tone: 'out', text: 'Not clocked in' };
  const label = m.clock === 'in' ? 'Clocked in' : 'On lunch';
  const here = m.at !== null && m.at.fiId !== null && facilityFiId !== null && m.at.fiId === facilityFiId;
  const where = m.at && !here ? ` at ${m.at.siteName}` : '';
  const since = m.sinceLocal ? ` · since ${m.sinceLocal}` : '';
  return { tone: m.clock, text: `${label}${where}${since}` };
}

/**
 * "6:35 PM", on the viewer's clock. Built from parts so the space before
 * AM/PM is an ordinary one: newer ICU emits a narrow no-break space there.
 */
export function formatAsOf(iso: string, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(new Date(iso));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
}

/**
 * The panel footer's source line, or null for none.
 *
 * `shown` is how many rows on the panel carry a status. With none, an `ok`
 * gets no line: crediting PunchWISE for a panel with nothing from PunchWISE on
 * it would print on every page of every store that is not on PunchWISE yet.
 * `unavailable` is said whatever is shown, because nothing is.
 */
export function clockFooter(view: TeamClockView | null | undefined, shown: number, timeZone?: string): string | null {
  if (!view || view.state === 'off') return null;
  if (view.state === 'unavailable') return 'Clock status from PunchWISE isn’t available right now.';
  if (shown === 0 || !view.asOf) return null;
  return `Clock status from PunchWISE · as of ${formatAsOf(view.asOf, timeZone)}`;
}

/** The slice of `document` the poller needs. */
export interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', fn: () => void): void;
  removeEventListener(type: 'visibilitychange', fn: () => void): void;
}

export interface PollerTimers {
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

const REQUEST_FAILED: TeamClockView = {
  state: 'unavailable',
  cause: 'request_failed',
  detail: 'FacilityWISE could not fetch the clock status just now.',
};

/**
 * Ask now and every `intervalMs` while the tab is VISIBLE; stop while hidden.
 *
 * - Becoming visible asks at once — a status that sat in a background tab is
 *   refreshed before anyone reads it.
 * - A tick while a request is still out is skipped, never stacked.
 * - `off` stops it for good: the feature is not configured, and asking every
 *   minute would not change that.
 * - A failed request reads as unavailable even after a good answer. The old
 *   statuses are NOT kept: "clocked in since 9:02" from ten minutes ago is a
 *   claim about now that nobody has checked.
 *
 * Returns `stop`, which drops any answer still on its way.
 */
export function startClockPoller(opts: {
  load: () => Promise<TeamClockResponse>;
  onAnswer: (view: TeamClockView) => void;
  doc: VisibilitySource;
  intervalMs?: number;
  timers?: PollerTimers;
}): () => void {
  const intervalMs = opts.intervalMs ?? CLOCK_REFRESH_MS;
  const timers: PollerTimers = opts.timers ?? {
    setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
    clearInterval: (h) => globalThis.clearInterval(h as ReturnType<typeof globalThis.setInterval>),
  };
  let handle: unknown = null;
  let inFlight = false;
  let stopped = false;

  const clear = () => {
    if (handle !== null) timers.clearInterval(handle);
    handle = null;
  };

  const tick = () => {
    if (stopped || inFlight) return;
    inFlight = true;
    opts
      .load()
      .then(
        (answer) => {
          if (stopped) return;
          opts.onAnswer(answer);
          if (answer.state === 'off') stop();
        },
        () => {
          if (!stopped) opts.onAnswer(REQUEST_FAILED);
        },
      )
      .finally(() => {
        inFlight = false;
      });
  };

  const resume = () => {
    tick();
    if (handle === null && !stopped) handle = timers.setInterval(tick, intervalMs);
  };

  const onVisibility = () => {
    if (stopped) return;
    if (opts.doc.visibilityState === 'visible') resume();
    else clear();
  };

  function stop() {
    stopped = true;
    clear();
    opts.doc.removeEventListener('visibilitychange', onVisibility);
  }

  opts.doc.addEventListener('visibilitychange', onVisibility);
  if (opts.doc.visibilityState === 'visible') resume();
  return stop;
}
